import {
  Connection,
  Keypair,
  Transaction,
  type TransactionInstruction,
} from '@solana/web3.js'
import type { WalletContextState } from '@solana/wallet-adapter-react'
import { PUBLIC_SOLANA_RPC, RPC_ENDPOINT, TOKEN } from './constants'

export type TokenFormParams = {
  name: string
  symbol: string
  description: string
  website?: string
  /** Public HTTPS metadata JSON uri (from uploadTokenMetadata / IPFS). */
  uri: string
}

export type CreateCoinResult = {
  mint: string
  signature: string
  pumpUrl: string
}

/**
 * Create a token on pump.fun mainnet via official SDK (on-site launch path).
 * Name / symbol / uri go on-chain via createV2Instruction.
 * Call uploadTokenMetadata first so uri JSON includes description / website / image.
 * Wallet pays fees and signs; mint keypair is generated in-browser (never persisted).
 * pump-sdk is dynamically imported so the heavy chunk loads only when Launch runs.
 */
export async function createTokenOnPump(
  connection: Connection,
  wallet: WalletContextState,
  params: TokenFormParams,
): Promise<CreateCoinResult> {
  if (!wallet.publicKey) {
    throw new Error('Connect a Solana wallet first (Phantom or Solflare).')
  }
  if (!wallet.signTransaction && !wallet.sendTransaction) {
    throw new Error('Connected wallet cannot sign transactions.')
  }

  const name = params.name.trim()
  const symbol = params.symbol.trim()
  const uri = (params.uri || TOKEN.metadataUri).trim()

  if (!name) throw new Error('Token name is required.')
  if (!symbol) throw new Error('Token symbol is required.')
  if (symbol.length > 10) {
    throw new Error('Symbol must be 10 characters or fewer.')
  }
  const description = params.description.trim()
  if (!description) {
    throw new Error('Description is required.')
  }
  const website = params.website?.trim()
  if (website) {
    try {
      const u = new URL(website)
      if (u.protocol !== 'http:' && u.protocol !== 'https:') {
        throw new Error('bad protocol')
      }
    } catch {
      throw new Error('Website must be a valid http(s) URL.')
    }
  }
  if (!uri) throw new Error('Metadata URI is required.')

  // description / website validated here; they must already be baked into `uri`
  // via uploadTokenMetadata. createV2Instruction on-chain fields: name, symbol, uri.

  const { PUMP_SDK } = await import('@pump-fun/pump-sdk')

  const mint = Keypair.generate()
  const user = wallet.publicKey

  const ix: TransactionInstruction = await PUMP_SDK.createV2Instruction({
    mint: mint.publicKey,
    name,
    symbol,
    uri,
    creator: user,
    user,
    mayhemMode: false,
    cashback: false,
  })

  let activeConnection = connection
  let blockhash: string
  let lastValidBlockHeight: number
  try {
    ;({ blockhash, lastValidBlockHeight } =
      await activeConnection.getLatestBlockhash('confirmed'))
  } catch (e) {
    // A configured RPC can be rate-limited or blocked by the host. Retry once
    // against the free public endpoint before surfacing the error to the user.
    if (RPC_ENDPOINT !== PUBLIC_SOLANA_RPC) {
      try {
        activeConnection = new Connection(PUBLIC_SOLANA_RPC, 'confirmed')
        ;({ blockhash, lastValidBlockHeight } =
          await activeConnection.getLatestBlockhash('confirmed'))
      } catch (fallbackError) {
        const detail =
          fallbackError instanceof Error
            ? fallbackError.message
            : String(fallbackError)
        throw new Error(`RPC failed while fetching blockhash: ${detail}`)
      }
    } else {
      const detail = e instanceof Error ? e.message : String(e)
      throw new Error(`RPC failed while fetching blockhash: ${detail}`)
    }
  }

  const tx = new Transaction({
    feePayer: user,
    blockhash,
    lastValidBlockHeight,
  }).add(ix)

  let signature: string

  // Prefer sendTransaction + signers so the adapter attaches the mint signature
  // without relying on partialSign surviving wallet re-serialization.
  try {
    if (wallet.sendTransaction) {
      signature = await wallet.sendTransaction(tx, activeConnection, {
        signers: [mint],
        skipPreflight: false,
        preflightCommitment: 'confirmed',
        maxRetries: 3,
      })
    } else if (wallet.signTransaction) {
      tx.partialSign(mint)
      const signed = await wallet.signTransaction(tx)
      signature = await activeConnection.sendRawTransaction(signed.serialize(), {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
      })
    } else {
      throw new Error('No send/sign method on wallet.')
    }
  } catch (e) {
    const detail = e instanceof Error ? e.message : String(e)
    // Re-throw with original message so LaunchPanel can map user-facing copy.
    throw new Error(detail)
  }

  try {
    await activeConnection.confirmTransaction(
      { signature, blockhash, lastValidBlockHeight },
      'confirmed',
    )
  } catch (e) {
    const detail = e instanceof Error ? e.message : String(e)
    throw new Error(`RPC failed while confirming transaction: ${detail}`)
  }

  const mintStr = mint.publicKey.toBase58()
  return {
    mint: mintStr,
    signature,
    pumpUrl: `https://pump.fun/coin/${mintStr}`,
  }
}

/** @deprecated Prefer createTokenOnPump — kept as alias for older call sites. */
export const createExfundOnPump = createTokenOnPump
